/**
 * Utility functions for formatting search filters for SEO
 */

/**
 * Format age value to human-readable format
 */
const formatAge = (age) => {
  const ageMap = {
    '2-4-months': '2-4 months',
    '5-12-months': '5-12 months',
    '1-2-years': '1-2 years',
    '3-5-years': '3-5 years',
    'older-than-5-years': '5+ years',
  };
  return ageMap[age] || age.replace(/-/g, ' ');
};

/**
 * Format color value to human-readable format (capitalize)
 */
const formatColor = (color) => {
  return color
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format ad type value to human-readable format
 */
const formatAdType = (adType) => {
  const adTypeMap = {
    'for-sale': 'For Sale',
    'for-adoption': 'For Adoption',
    'for-stud': 'For Stud',
    'wanted': 'Wanted',
  };
  return adTypeMap[adType] || adType.replace(/-/g, ' ');
};

/**
 * Format temperament value
 */
const formatTemperament = (temperament) => {
  return temperament
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format compatibility value
 */
const formatCompatibility = (compatibility) => {
  const compatibilityMap = {
    'okay-with-cats': 'Cats',
    'okay-with-dogs': 'Dogs',
    'okay-with-kids': 'Kids',
    'okay-with-seniors': 'Seniors',
    'okay-with-apartments': 'Apartments',
  };
  return compatibilityMap[compatibility] || compatibility.replace(/-/g, ' ');
};

/**
 * Format gender value
 */
const formatGender = (gender) => {
  return gender.charAt(0).toUpperCase() + gender.slice(1);
};

/**
 * Format litter habits
 */
const formatLitterHabits = (level) => {
  const levelMap = {
    'litter-trained': 'Litter trained',
    'not-litter-trained': 'Not litter trained',
  };
  return levelMap[level] || level.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

/**
 * Format energy level
 */
const formatEnergyLevel = (level) => {
  const levelMap = {
    'low': 'Low',
    'medium': 'Medium',
    'high': 'High',
  };
  return levelMap[level] || level.charAt(0).toUpperCase() + level.slice(1);
};

/**
 * Format grooming level
 */
const formatGroomingLevel = (level) => {
  const levelMap = {
    'low': 'Low',
    'medium': 'Medium',
    'high': 'High',
  };
  return levelMap[level] || level.charAt(0).toUpperCase() + level.slice(1);
};

/**
 * Format advertiser type
 */
const formatAdvertiserType = (type) => {
  const typeMap = {
    'individual': 'Individual',
    'breeder': 'Breeder',
    'company': 'Company',
  };
  return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Format a single filter value based on its key
 */
export const formatFilterValue = (key, value, adOptions = {}) => {
  // If we have adOptions, try to use them first
  if (adOptions[key]?.options && adOptions[key].options[value]) {
    return adOptions[key].options[value];
  }

  // Otherwise use formatting functions
  switch (key) {
    case 'age':
      return formatAge(value);
    case 'color':
      return formatColor(value);
    case 'ad_type':
      return formatAdType(value);
    case 'temperament':
      return formatTemperament(value);
    case 'compatibility':
      return formatCompatibility(value);
    case 'gender':
      return formatGender(value);
    case 'litter-habits':
      return formatLitterHabits(value);
    case 'energy-level':
      return formatEnergyLevel(value);
    case 'grooming-level':
      return formatGroomingLevel(value);
    case 'advertiser-type':
      return formatAdvertiserType(value);
    default:
      // Default: capitalize and replace hyphens
      return value
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  }
};

/**
 * Format array of filter values
 */
export const formatFilterValues = (key, values, adOptions = {}) => {
  if (typeof values === 'string') {
    values = values.split(',');
  }
  return values.map(value => formatFilterValue(key, value.trim(), adOptions));
};

/**
 * Generate SEO title from search parameters
 */
export const generateSearchTitle = (searchParams, adOptions = {}) => {
  const parts = [];
  const params = Object.fromEntries(searchParams.entries());

  // Add ad type if present
  if (params.ad_type) {
    const adTypes = formatFilterValues('ad_type', params.ad_type, adOptions);
    parts.push(...adTypes);
  }

  // Add age if present
  if (params.age) {
    const ages = formatFilterValues('age', params.age, adOptions);
    parts.push(...ages);
  }

  // Add color if present
  if (params.color) {
    const colors = formatFilterValues('color', params.color, adOptions);
    parts.push(...colors);
  }

  // Add gender if present
  if (params.gender) {
    const genders = formatFilterValues('gender', params.gender, adOptions);
    parts.push(...genders);
  }

  // Build title - limit to first 3 parts to keep it readable
  let title = 'Sphynx Cats';
  if (parts.length > 0) {
    const titleParts = parts.slice(0, 3);
    title = titleParts.join(' ') + ' Sphynx Cats';
    // Limit title length
    if (title.length > 60) {
      const shortParts = parts.slice(0, 2);
      title = shortParts.join(' ') + ' Sphynx Cats';
    }
  }

  title += ' - SphynxPlace.com';

  return title;
};

/**
 * Generate SEO description from search parameters
 */
export const generateSearchDescription = (searchParams, adOptions = {}, totalResults = null) => {
  const parts = [];
  const params = Object.fromEntries(searchParams.entries());
  const filters = [];

  // Add ad type
  if (params.ad_type) {
    const adTypes = formatFilterValues('ad_type', params.ad_type, adOptions);
    filters.push(adTypes.join(', '));
  }

  // Add age
  if (params.age) {
    const ages = formatFilterValues('age', params.age, adOptions);
    filters.push(`Age: ${ages.join(', ')}`);
  }

  // Add color
  if (params.color) {
    const colors = formatFilterValues('color', params.color, adOptions);
    filters.push(`Color: ${colors.join(', ')}`);
  }

  // Add gender
  if (params.gender) {
    const genders = formatFilterValues('gender', params.gender, adOptions);
    filters.push(`Gender: ${genders.join(', ')}`);
  }

  // Add temperament
  if (params.temperament) {
    const temperaments = formatFilterValues('temperament', params.temperament, adOptions);
    filters.push(`Temperament: ${temperaments.join(', ')}`);
  }

  // Add compatibility
  if (params.compatibility) {
    const compatibilities = formatFilterValues('compatibility', params.compatibility, adOptions);
    filters.push(`Compatible with: ${compatibilities.join(', ')}`);
  }

  // Add price range
  if (params.price_from || params.price_to) {
    const priceParts = [];
    if (params.price_from) priceParts.push(`from $${params.price_from}`);
    if (params.price_to) priceParts.push(`to $${params.price_to}`);
    if (priceParts.length > 0) {
      filters.push(`Price: ${priceParts.join(' ')}`);
    }
  }

  // Build description
  let description = 'Find ';
  
  if (filters.length > 0) {
    description += filters.join(', ') + ' Sphynx cats';
  } else {
    description += 'Sphynx cats';
  }

  description += ' on SphynxPlace.com. Browse verified listings from trusted breeders.';

  if (totalResults !== null) {
    description += ` ${totalResults} ${totalResults === 1 ? 'listing found' : 'listings found'}.`;
  }

  return description;
};

/**
 * Generate keywords from search parameters
 */
export const generateSearchKeywords = (searchParams, adOptions = {}) => {
  const keywords = ['Sphynx cats', 'Sphynx'];
  const params = Object.fromEntries(searchParams.entries());

  // Add ad type
  if (params.ad_type) {
    const adTypes = formatFilterValues('ad_type', params.ad_type, adOptions);
    keywords.push(...adTypes);
  }

  // Add age
  if (params.age) {
    const ages = formatFilterValues('age', params.age, adOptions);
    ages.forEach(age => keywords.push(`Sphynx ${age}`));
  }

  // Add color
  if (params.color) {
    const colors = formatFilterValues('color', params.color, adOptions);
    colors.forEach(color => keywords.push(`${color} Sphynx`));
  }

  return keywords.join(', ');
};

